<template>
  <div
    class="custom-animation tw-relative tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)] tw-flex tw-flex-col tw-rounded-[4px] tw-bg-[#FCFCFD] tw-border tw-border-solid tw-border-[#EFF0F1] sm:tw-min-w-[512px] max-sm:tw-max-w-[370px] sm:tw-h-[370px]"
  >
    <!-- title -->
    <div
      id="dash-cyber-health-score-title"
      class="tw-flex tw-justify-between tw-items-center tw-h-[66px] tw-px-[20px] tw-py-[25px] tw-border-0 tw-border-b tw-border-solid tw-border-[#EFF0F1] tw-gap-2"
    >
      <IconCyberHealthScore />
      <span
        class="title tw-w-full tw-text-[#211F58] tw-text-[18px] tw-font-semibold tw-truncate"
      >
        {{
          getComponentUiText(
            "app-assess-recommendations-cyber-health-score-title-1",
          )
        }}
      </span>
    </div>

    <div class="tw-flex tw-flex-col tw-flex-1">
      <div
        v-if="isLoading"
        class="custom-global-skeleton tw-w-full tw-h-full tw-min-h-[250px]"
      />
      <template v-else>
        <!-- card -->
        <div
          class="tw-relative tw-flex tw-flex-1 tw-overflow-hidden tw-justify-center tw-min-h-[250px]"
        >
          <CyberHealthScoreChart
            :current-score="
              !isShowDummyCard
                ? metricData?.checklistScore
                : dummyData.checklistScore
            "
            :potential-score="totalAddScore"
            :total-score="
              !isShowDummyCard
                ? metricData?.checklistScoreMax
                : dummyData.checklistScoreMax
            "
            :pcnt-score="(metricData?.checklistScorePcnt || 0) * 100"
          />
        </div>
        <!-- footer -->
        <div class="tw-flex tw-gap-5 tw-p-[20px] tw-pt-0 max-sm:tw-flex-col">
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="dot tw-bg-[#387DFF]" />
            <span class="foot-text">
              {{
                getComponentUiText(
                  "app-assess-recommendations-cyber-health-score-sub-title-1",
                )
              }}
            </span>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="dot tw-bg-[#D9EDE2]" />
            <span class="foot-text">
              {{
                getComponentUiText(
                  "app-assess-recommendations-cyber-health-score-sub-title-2",
                )
              }}
            </span>
          </div>
          <!--          <div -->
          <!--            class="tw-flex tw-flex-1 sm:tw-justify-end tw-items-center tw-gap-2 tw-cursor-pointer" -->
          <!--          > -->
          <!--            <span class="foot-text !tw-text-[#387DFF]"> -->
          <!--              {{ -->
          <!--                getComponentUiText( -->
          <!--                  "app-assess-recommendations-tip-top-link-1", -->
          <!--                ).slice(0, -1) -->
          <!--              }} -->
          <!--            </span> -->
          <!--            <IconLearnMore /> -->
          <!--          </div> -->
        </div>
      </template>
    </div>

    <DummyCard
      v-if="isShowDummyCard && !isLoading"
      :title="
        getComponentUiText('app-dashboard-dummy-title-cyber-health-score-1')
      "
      :title-button="
        getComponentUiText('app-assessment-button-create-button-2')
      "
      :title-class="'tw-w-[276px]'"
      @button:click="() => emits('button:click')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import IconCyberHealthScore from "@/components/svgs/iconCyberHealthScore.vue";
import CyberHealthScoreChart from "@/components/chart/cyberHealthScoreChart.vue";
import DummyCard from "@/components/dashboard/dummyCard.vue";

import store from "@/store";

import { getComponentUiText } from "@/utils/utilities";
import { TTypeMetric } from "@/interfaces/recommendations/IActionInterfaces";

type TypeGroupMetric = {
  checklistGroupScore: number;
  checklistGroupScoreMax: number;
}[];

const props = defineProps({
  metric: {
    type: Object as () => TTypeMetric,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const dummyData = ref({
  checklistScore: 86,
  checklistScoreMax: 100,
});

const checklistId = computed(
  () => store.getters["Recommend/getLatestChecklistId"],
);

const isShowDummyCard = computed(
  () => !isLoading.value && (!metricData.value || !checklistId.value),
);

const emits = defineEmits(["button:click"]);
const metricData = ref<TTypeMetric>(props.metric);
const isLoading = ref(props.loading);

const checklistGroupMetrics = computed<TypeGroupMetric>(
  () => store.state.Recommend.checklistGroupMetrics,
);
const totalAddScore = computed(() =>
  checklistGroupMetrics.value.reduce((previousValue, currentValue) => {
    const addScore =
      (currentValue.checklistGroupScoreMax || 0) -
      (currentValue.checklistGroupScore || 0);
    return previousValue + addScore;
  }, 0),
);

watch(
  () => props.metric,
  (newVal) => {
    metricData.value = newVal;
  },
);

watch(
  () => props.loading,
  (newVal) => {
    isLoading.value = newVal;
  },
);
</script>

<style lang="scss" scoped>
.title {
  font-family: Poppins, sans-serif;
}
.dot {
  width: 9px;
  height: 9px;
  border-radius: 9999px;
}
.foot-text {
  font-family: Poppins, sans-serif;
  color: #878787;
  font-weight: 400;
  font-size: 14px;
}
</style>
